import { MaintenanceWindowFormView } from '../view/MaintenanceWindowFormView';
import { templateMaintenanceWindowForm } from '../view/template';
import { MaintenanceWindowFormRepository } from '../repository/MaintenanceWindowFormRepository';
import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { MaintenanceWindowFormWebComponent } from '..';
import { MaintenanceWindowData, MaintenanceWindowFormData } from '../types';
import { MaintenanceWindowsEvents } from '../../maintenanceWindowGrid/types';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { UiToDbValuesConverter } from '../../../utils/maintenanceWindow/patchManagement/UiToDbValuesConverter';
import { Rights } from '@nirvana/shared/constants';

export class MaintenanceWindowFormController extends BaseWebComponentController {
  public component: MaintenanceWindowFormWebComponent;
  public dataRepository: MaintenanceWindowFormRepository;
  public view: MaintenanceWindowFormView;
  public viewConstructor: typeof MaintenanceWindowFormView;
  public dataRepositoryConstructor: typeof MaintenanceWindowFormRepository;

  constructor(component: MaintenanceWindowFormWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: MaintenanceWindowFormView,
      viewTemplate: templateMaintenanceWindowForm,
      dataRepositoryConstructor: MaintenanceWindowFormRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$btnSave,
        event: 'click',
        callback: this.saveNewMaintenanceWindow,
      },
      {
        element: this.view.dom.$btnCancel,
        event: 'click',
        callback: this.closeMaintenanceWindowForm,
      },
    ];
  }

  private async saveNewMaintenanceWindow(): Promise<void> {
    const errors: Array<any> = [];

    if (!this.view.formGroup.valid) {
      this.view.formGroup.validate();
      return;
    }

    this.view.dom.$btnSave.showSpinner = true;
    this.view.dom.$btnSave.disabled = true;

    const record: Object = this.view.formGroup.value;

    record['company'] = {
      id: this.dependencies.getActiveCompany().companyId,
      name: this.dependencies.getActiveCompany().companyName,
    };

    // Even when the selector is disabled/hidden, and patches remain selected, the new selection must be saved
    record['specifics']['manuallyApprovedPatchIds'] =
      this.view.dom.$patchSpecificsComponent.getManuallyApprovedPatchIds();

    try {
      if (this.component.maintenanceWindowId) {
        if (this.view.dom.$patchSpecificsComponent.isVendorProductPairsEnabled()) {
          record['specifics']['vendorProductsPairs'] = this.view.dom.$patchSpecificsComponent.getVendorProductsPairs();
        }
        if (this.view.dom.$patchSpecificsComponent.isCachingServersEnabled()) {
          record['specifics']['cachingServers'] = this.view.dom.$patchSpecificsComponent.getCachingServers();
        }
        record['specifics'] = UiToDbValuesConverter.prepareSpecificsOptions(record['specifics'], false);
        record['_id'] = this.component.maintenanceWindowId;

        await this.dataRepository.updateMaintenanceWindow(record);
        this.showSuccessToast(this.dependencies.translateFn('Maintenance window updated successfully.'));
      } else {
        record['specifics']['vendorProductsPairs'] =
          this.view.dom.$patchSpecificsComponent.isVendorProductPairsEnabled()
            ? this.view.dom.$patchSpecificsComponent.getVendorProductsPairs()
            : [];
        record['specifics']['cachingServers'] = this.view.dom.$patchSpecificsComponent.isCachingServersEnabled()
          ? this.view.dom.$patchSpecificsComponent.getCachingServers()
          : [];
        record['specifics'] = UiToDbValuesConverter.prepareSpecificsOptions(record['specifics'], true);

        await this.dataRepository.createMaintenanceWindow(record);
        this.showSuccessToast(this.dependencies.translateFn('Maintenance window created successfully.'));
      }
    } catch (error) {
      // if the exception is AuthenticationSecurityException it is already treated by the general session exception
      if (error?.exceptionType === 'Exceptions\\AuthenticationSecurityException') {
        return;
      }
      errors.push(error);

      this.showErrorToast(error);
    } finally {
      // remove the spinner
      this.view.dom.$btnSave.showSpinner = false;
      this.view.dom.$btnSave.disabled = false;
    }

    if (!errors.length) {
      this.dispatchSuccessEvent();
      await this.closeMaintenanceWindowForm();
    }

    this.view.dom.$btnSave.disabled = false;
  }

  private dispatchSuccessEvent() {
    // reload grid after successfully save MW
    this.component.dispatchEvent(
      new CustomEvent(MaintenanceWindowsEvents.RELOAD_GRID, {
        detail: {},
        bubbles: true,
      })
    );
  }

  private async closeMaintenanceWindowForm() {
    this.component.isVisible = false;
    this.view.unsubscribeObservers();
    await this.view.resetFormToDefault();
  }

  async getDataForView(): Promise<MaintenanceWindowFormData> {
    this.view.setDisabledInnerComponents(false);
    const maintenanceWindowsTypes = this.dataRepository.getMaintenanceWindowsTypes();
    let maintenanceWindowData, maintenanceWindowType, currentUserIsOwner;
    maintenanceWindowData = this.component.maintenanceWindowId
      ? this.dataRepository.getMaintenanceWindow(this.component.maintenanceWindowId)
      : ({} as MaintenanceWindowData);

    const viewData = await Promise.all([maintenanceWindowsTypes, maintenanceWindowData]);

    maintenanceWindowType = viewData[1]?.type ?? this.component.maintenanceWindowType;

    if (!this.component.maintenanceWindowId) {
      viewData[1].name = await this.dataRepository.getSuggestedNewMaintenanceWindowName(maintenanceWindowType);
    } else {
      currentUserIsOwner = this.dependencies.getCurrentUser()._id === viewData[1]?.ownerId;
      if (!viewData[1].allowChangeByOtherUsers && !currentUserIsOwner) {
        this.view.setDisabledInnerComponents(true);
      }
    }

    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.view.setDisabledInnerComponents(true);
    }

    return {
      maintenanceWindowsTypes: viewData[0],
      maintenanceWindowData: viewData[1],
      maintenanceWindowType: maintenanceWindowType,
      currentUserIsOwner: currentUserIsOwner,
    };
  }

  private showSuccessToast(message: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.SUCCESS,
    });
  }

  private showErrorToast(error: Error) {
    const errorMessage = error?.message || this.translator.getGenericErrorMessage();

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: ToastSeverity.ERROR,
    });
  }
}
